import { useRender } from "@base-ui/react/use-render";
import type { ReactElement } from "react";

import { cn } from "../foundations/cn.js";
import { CARD_INTERACTIVE, STRETCHED_LINK } from "../foundations/interaction.js";
import { useLinkRender } from "../foundations/linkContext.js";
import { Amount } from "./Amount.js";
import { ItemArt } from "./ItemArt.js";

export interface HitChipProps {
  itemName: string;
  image: string;
  /** Who pulled it, as their public name. */
  username: string;
  /** Integer cents. */
  value: number;
  /** `glass` on its own; `plain` inside a bar that is already glass. */
  surface?: "glass" | "plain";
  /** Links to the box it was pulled from (a stretched link on the item's name). */
  href?: string;
  /** The app's router link. */
  render?: ReactElement;
  className?: string;
}

/**
 * One recent hit: the item and who pulled it, with its value beside them. With `href` it links to
 * the box it came from; without one it is static. Only hits worth celebrating.
 */
export function HitChip({
  itemName,
  image,
  username,
  value,
  surface = "glass",
  href,
  render,
  className,
}: HitChipProps) {
  const linked = Boolean(href || render);
  const nameLink = useRender({
    render: useLinkRender(render),
    defaultTagName: "a",
    enabled: linked,
    props: { href, className: STRETCHED_LINK, children: itemName },
  });
  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center gap-2.5 rounded-lg border border-transparent py-1.5 pr-3.5 pl-1.5",
        surface === "glass" ? "glass" : "bg-bg-raised/50",
        linked && CARD_INTERACTIVE,
        className,
      )}
    >
      <ItemArt src={image} alt="" className="h-10 w-7 shrink-0 rounded-[3px]" />
      <span className="flex min-w-0 flex-col">
        <span className="max-w-32 truncate font-body text-body-b3-strong text-text-primary">
          {linked ? nameLink : itemName}
        </span>
        <span className="truncate font-mono text-mono-meta text-text-tertiary">@{username}</span>
      </span>
      <Amount cents={value} trimZeroCents className="pl-1 text-text-primary" />
    </div>
  );
}
